<template>
  <auth-layout container-class="xl:max-w-2xl">
    <template #panel>
      <div></div>
    </template>
    <div
      v-if="success"
      class="text-center my-12 border border-positive-500 bg-positive-100 p-6 rounded text-positive-800"
    >
      <h1 class="text-2xl font-semibold mb-4">Registration Successful</h1>
      <p>Thank you for applying to be a delivery partner. We will be in touch soon.</p>
      <p>
        <icon-link :href="route('login')">
          Return to Login
        </icon-link>
      </p>
    </div>
    <div v-else>
      <div class="text-left content" v-html="parseMarkdown(content)"></div>
      <form
        v-if="!success"
        enctype="multipart/form-data"
        novalidate
        class="grid w-full max-w-lg mx-auto"
        @submit.prevent="submitForm"
      >
        <div class="pt-2 pb-6 col-span-full">
          <div class="text-center border-b border-grey-200">
            <p class="font-semibold -mb-3">
              <span class="bg-white px-3">Your Application for {{ $config.name }} {{ $config.current_wave }}</span>
            </p>
          </div>
        </div>
        <auth-input
          v-model="form.name"
          label="Name"
          input-type="text"
          :error="getError('name')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          class="col-span-3"
        />

        <auth-input
          v-model="form.email"
          label="Email"
          input-type="email"
          :error="getError('email')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          placeholder="e.g. your.name@domain.com"
          class="col-span-2"
        />

        <auth-input
          v-model="form.company_name"
          label="Employer / Company Name"
          input-type="text"
          :error="getError('company_name')"
          :required="true"
          :loading="loading"
          :autocomplete="true"
          class="col-span-3"
        />

        <auth-textarea
          v-model="form.address"
          label="Company Address"
          :error="getError('address')"
          :required="true"
          :rows="5"
          :loading="loading"
          class="col-span-full"
        />

        <div class="col-span-full">
          <label class="form-label">
            Partner Category
          </label>

          <select
            v-model="form.category"
            class="block w-full border border-grey-200 p-3 mt-2"
            :disabled="loading"
            required
          >
            <option value="" disabled>
              Please select…
            </option>
            <option v-for="cat in state.categories" :value="cat" :key="cat">
              {{ cat }}
            </option>
          </select>

        </div>

        <div class="pt-2 pb-6 col-span-full">
          <div class="text-center border-b border-grey-200">
            <p class="font-semibold -mb-3">
              <span class="bg-white px-3">Proposal</span>
            </p>
          </div>
        </div>

        <div class="col-span-full text-left">
          <auth-input
            id="file_upload"
            @input="form.file_upload = $event"
            label="Upload file(s)"
            input-type="file"
            :error="getError('file_upload')"
            :required="true"
            :loading="loading"
            :multiple="true"
            class="col-span-full"
          >
            <p class="text-sm italic mt-1">
              Please provide up to three uploads each being a max file size of 2mb. We accept pdf and xlsx.
            </p>
          </auth-input>
          <hr class="border-b-2 my-12">
          <label class="form-label">Confirmation</label>
          <input-checkbox
            v-model="form.confirmation"
            :value="true"
            :error="getError('confirmation')"
            required
            class="mt-6"
            label-class="text-base"
          >
            I confirm that I agree to Mobilise's Terms & Conditions.
          </input-checkbox>
        </div>

        <app-button
          class="col-span-2 my-8"
          type="submit"
          :loading="loading"
          block
        >
          Submit Application
        </app-button>
      </form>
    </div>
  </auth-layout>
</template>

<script>
import { requestMixin } from '../mixins/request';

const storageKey = 'delivery_partner_registration_form';
const defaults = {
  name: "",
  email: "",
  company_name: "",
  address: "",
  category: "",
  confirmation: false,
  file_upload: [],
};
export default {
  name: 'DeliveryPartnerRegistration',
  mixins: [requestMixin],
  data() {
    return {
      success: false,
      loading: false,
      form: { ...defaults },
    }
  },
  computed: {
    content() {
      return this.state?.content
    },
  },
  watch: {
    form: {
      deep: true,
      handler: function() {
        localStorage.setItem(storageKey, JSON.stringify({...this.form, file_upload: null}));
      },
    },
  },
  created() {
    const savedForm = localStorage.getItem(storageKey);
    if (savedForm) {
      this.form = JSON.parse(savedForm);
    }
  },
  methods: {
    submitForm(event) {
      this.loading = true;
      const formData = new FormData();

      for (const key in this.form) {
        if (key !== 'file_upload') {
          formData.append(key, this.form[key]);
        }
      }

      if (Array.isArray(this.form.file_upload)) {
        this.form.file_upload.forEach((file, index) => {
          formData.append(`file_upload[${index}]`, file);
        });
      }

      const params = new URLSearchParams(window.location.search);

      if(params.has('signature')) {
        formData.append('signature', params.get('signature'));
      }

      axios.post(window.location.pathname, formData)
        .then(() => {
          this.success = true;
          this.form = { ...defaults };
          localStorage.removeItem(storageKey);
          this.$el.parentElement.scrollTo({ top: 0, behavior: 'smooth' });
        })
        .catch(error => this.requestError(error))
        .then(() => {
          event.target.scrollIntoView({ behavior: 'smooth' });
          this.loading = false;
        });
    },
  },
}
</script>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1rem;
}

.col-span-full {
  grid-column: 1 / -1;
}

.col-span-3 {
  grid-column: span 3 / span 3;
}

.col-span-2 {
  grid-column: span 2 / span 2;
}
</style>

